import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import type { Session } from '../../api/session';
import { createPass, createPassRequest, getObject, getObjectAddresses, type NewPass, type ObjectInfo } from '../../api/tenant';
import { passOptions } from '../../domain/availability';
import { formatServerDate, moscowTime } from '../../domain/moscowTime';
import { describePassError } from '../../domain/passErrors';
import { GuestType } from '../../domain/passes';
import { normalizePlate } from '../../domain/plate';
import { QUICK_MINUTES, quickPlan, quickPurpose, quickWindow, type QuickKind } from '../../domain/quick';
import type { TelegramApi } from '../../telegram/webapp';
import { Chips } from '../../ui/Choice';
import { Skeleton } from '../../ui/Skeleton';
import { MessageScreen } from '../auth/screens';
import { LoadFailed } from '../common/LoadFailed';
import s from './passForm.module.css';

interface Props {
  session: Session;
  telegram: TelegramApi;
  object: ObjectInfo;
  kind: QuickKind;
}

const TEXT: Record<QuickKind, { title: string; name: string; who: string }> = {
  taxi: { title: 'Такси', name: 'Такси', who: 'такси' },
  courier: { title: 'Курьер', name: 'Курьер', who: 'курьеру' },
};

/**
 * «Такси или курьер в один тап» (ideas 2026-10-13 #5): a one-time pass for the next hour. A taxi needs only
 * the plate from the taxi app; a courier on foot needs nothing at all.
 */
export function QuickPassScreen({ session, telegram, object, kind }: Props) {
  const queryClient = useQueryClient();
  const addresses = useQuery({ queryKey: ['addresses', object.id], queryFn: () => getObjectAddresses(session, object.id) });
  const details = useQuery({ queryKey: ['object', object.id], queryFn: () => getObject(session, object.id) });
  const plan = useMemo(() => quickPlan(kind, passOptions(details.data?.settings ?? {})), [kind, details.data]);
  const usable = useMemo(() => (addresses.data ?? []).filter((a) => !a.isBlocked), [addresses.data]);
  const text = TEXT[kind];

  const [addressId, setAddressId] = useState<number | null>(null);
  const [plate, setPlate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ request: boolean; expiresAt: Date } | null>(null);

  useEffect(() => {
    if (addressId === null && usable.length) setAddressId(usable[0].id);
  }, [usable, addressId]);

  const limitKey = plan?.duration === 'temporary' ? 'passesLimitHoursTemporary' : 'passesLimitHoursOneTime';
  const limitRaw = details.data?.settings[limitKey];
  const limitHours = typeof limitRaw === 'number' && limitRaw > 0 ? limitRaw : null;
  const request = plan?.mode === 'request';
  const preview = quickWindow(new Date(), limitHours, request);

  const submit = useMutation({
    mutationFn: async () => {
      // The window is computed at the tap, not when the screen opened.
      const window = quickWindow(new Date(), limitHours, request);
      const purpose = quickPurpose(kind, details.data?.options ?? []);
      const body: NewPass = {
        addressId: addressId!,
        durationType: plan!.duration === 'temporary' ? 2 : 1,
        guestType: plan!.guest === 'vehicle' ? GuestType.Vehicle : GuestType.Pedestrian,
        guestData: plan!.guest === 'vehicle' ? { plateNumber: plate } : { name: text.name },
        startsAt: formatServerDate(window.startsAt),
        expiresAt: formatServerDate(window.expiresAt),
        // Security sees who it is: the object's purpose when there is one, otherwise a comment.
        ...(purpose ? { options: [purpose.id] } : { comment: text.name }),
      };
      if (request) await createPassRequest(session, body);
      else await createPass(session, body);
      return window.expiresAt;
    },
    onSuccess: (expiresAt) => {
      telegram.notify('success');
      void queryClient.invalidateQueries({ queryKey: ['passes'] });
      setDone({ request, expiresAt });
    },
    onError: (e) => {
      telegram.notify('error');
      const described = describePassError(e);
      setError(described.fields.plate ?? described.general ?? 'Не удалось оформить пропуск');
    },
  });

  if (addresses.isError || details.isError) {
    return <LoadFailed error={addresses.error ?? details.error} onRetry={() => { void addresses.refetch(); void details.refetch(); }} fullScreen />;
  }
  if (!addresses.data || !details.data) {
    return (
      <main className={s.sheet} aria-busy="true">
        <Skeleton height={44} />
        <Skeleton height={120} />
      </main>
    );
  }
  if (!usable.length) {
    return <MessageScreen title="Заказ недоступен" text="Адрес заблокирован управляющей компанией. Позвоните в УК, чтобы узнать причину." />;
  }
  if (!plan) {
    return <MessageScreen title={`Пропуск ${text.who} недоступен`} text="На этом объекте жители не оформляют такие пропуска сами. Обратитесь в УК." />;
  }

  if (done) {
    return (
      <main className={s.done}>
        <h1 className={s.doneTitle}>{done.request ? 'Заявка отправлена' : 'Пропуск оформлен'}</h1>
        <p className={s.doneText}>
          {done.request
            ? `Пропуск ${text.who} начнёт действовать, когда УК его согласует.`
            : `${plan.guest === 'vehicle' ? `${plate} — ` : ''}пропуск ${text.who} действует до ${moscowTime(done.expiresAt)}.`}
        </p>
        <Link to="/" className={s.submit}>
          На главную
        </Link>
      </main>
    );
  }

  const onSubmit = () => {
    if (plan.guest === 'vehicle' && plate.length < 3) return setError('Введите госномер');
    setError(null);
    submit.mutate();
  };

  return (
    <main className={s.sheet} aria-labelledby="quick-title">
      <header className={s.head}>
        <h1 id="quick-title" className={s.title}>
          {text.title}
        </h1>
        <p className={s.where}>
          Разовый пропуск на {QUICK_MINUTES} минут · до {moscowTime(preview.expiresAt)}
        </p>
      </header>

      {addresses.data.length > 1 && (
        <Chips
          label="Адрес"
          value={addressId}
          items={addresses.data.map((a) => ({ id: a.id, label: a.isBlocked ? `${a.name} — заблокирован` : a.name, disabled: a.isBlocked }))}
          onChange={(v) => v !== null && setAddressId(v)}
        />
      )}

      {plan.guest === 'vehicle' ? (
        <div className={s.field}>
          <label htmlFor="quick-plate" className={s.label}>
            Госномер {kind === 'taxi' ? 'такси' : 'машины курьера'}
          </label>
          <input
            id="quick-plate"
            className={error ? `${s.plate} ${s.invalid}` : s.plate}
            value={plate}
            onChange={(e) => setPlate(normalizePlate(e.target.value))}
            placeholder="А000АА77"
            autoComplete="off"
            autoCapitalize="characters"
            autoFocus
            aria-invalid={!!error}
            aria-describedby="quick-plate-hint"
          />
          <span id="quick-plate-hint" className={s.hint}>
            {kind === 'taxi' ? 'Номер есть в приложении такси. ' : ''}Можно печатать латиницей — заменим на кириллицу
          </span>
        </div>
      ) : (
        <p className={s.caption}>Охрана увидит пешего гостя «{text.name}». Вписывать ничего не нужно.</p>
      )}

      <div className={s.footer}>
        {error && (
          <p className={s.error} role="alert">
            {error}
          </p>
        )}
        <button type="button" className={s.submit} onClick={onSubmit} disabled={submit.isPending}>
          {submit.isPending ? 'Оформляем…' : request ? 'Отправить на согласование' : `Пропустить ${text.who}`}
        </button>
        <Link to="/new" className={s.secondary}>
          Другой гость — полная форма
        </Link>
      </div>
    </main>
  );
}
